import { Row, Col, Input } from 'antd';
import { useState } from 'react';
import type { StoredTeamRole } from '../../../hooks/types';
import useDeleteTeamRole from '../../../hooks/useDeleteTeamRole';
import { teamRolesQueryName } from '../../../hooks/useQueryTeamRoles';
import useUpdateTeamRole from '../../../hooks/useUpdateTeamRole';
import SaveAndDeleteButtons from '../../SaveAndDeleteButtons';
import { RoleGroup, roleGroupTypes } from '../types';
import routes from '../../App/routes';
import Select from '../../AccessibleComponents/Select';
import { Optional } from '../../Shared';
import { successMessage } from '../../../lib/utils/messages';
import { useNotify } from '../../../hooks/useNotify';
import { onApolloError } from '../../../lib/graphql/helpers';

type TeamRoleUpdateFormProps = {
  readonly role: StoredTeamRole;
  readonly showRoleGroup: boolean;
};

/**
 * @returns A form for changing the name of a team role.
 */
const UpdateTeamRoleForm = (props: TeamRoleUpdateFormProps) => {
  const { notify } = useNotify();

  const [updateRole, { loading: updatingRole }] = useUpdateTeamRole(
    onApolloError(notify.error)
  );
  const [deleteRole, { loading: deletingRole }] = useDeleteTeamRole(
    onApolloError(notify.error)
  );

  const [roleName, setRoleName] = useState(props.role.name);
  const [roleGroup, setRoleGroup] = useState<RoleGroup | undefined>(
    props.role.role_group
  );

  return (
    <Row key={props.role.id}>
      <Col flex={'auto'}>
        <Input
          aria-label="Role Title"
          value={roleName}
          readOnly={props.role.read_only}
          onChange={(e) => setRoleName(e.target.value)}
        />
      </Col>
      <Optional show={props.showRoleGroup}>
        <Col flex={'120px'}>
          <Select
            className="role-group-select"
            disabled={props.role.read_only}
            id={`update-team-role-form-${props.role.id}`}
            onChange={(v) =>
              setRoleGroup(
                v.currentTarget.value === '' ? undefined : v.currentTarget.value
              )
            }
            value={roleGroup ?? ''}
            options={roleGroupTypes.filter((group) => group !== 'SOC')}
            title={'Role Group'}
            showEmptyOption={true}
          />
        </Col>
      </Optional>
      <Col flex={'100px'}>
        <Optional show={props.role.read_only === false}>
          <SaveAndDeleteButtons
            roleId={props.role.id}
            showSave
            showDelete
            routeOnDelete={routes.teamSettings}
            onSave={() => {
              void updateRole({
                variables: {
                  name: roleName,
                  roleGroup,
                  teamRoleId: props.role.id,
                },
                onCompleted: () => notify.success(successMessage.roleUpdated),
                // Note: We are choosing to currently refetch queries after update,
                // but a more efficient way to consider for the future may be to
                // directly update the cache after update.
                refetchQueries: [teamRolesQueryName],
              });
            }}
            saveButtonDisabled={
              roleName === props.role.name &&
              roleGroup === props.role.role_group
            }
            loading={updatingRole || deletingRole}
            onDelete={() => {
              void deleteRole({
                variables: { teamRoleId: props.role.id },
                onCompleted: () =>
                  notify.success(successMessage.roleDeactivated),
                // Note: We are choosing to currently refetch queries after update,
                // but a more efficient way to consider for the future may be to
                // directly update the cache after update.
                refetchQueries: [teamRolesQueryName],
              });
            }}
          />
        </Optional>
      </Col>
    </Row>
  );
};

export default UpdateTeamRoleForm;
